import { Link } from 'react-router';
import { PlaceholderArt } from './PlaceholderArt.jsx';
import { isLinkable } from '../../data/projects.js';
import styles from './ProjectCard.module.css';

/** Status label colours, keyed by the `tone` on a project's badge or bar. */
const BADGE_TONES = {
  available: styles.badgeAvailable,
  sale: styles.badgeSale,
};

const BAR_TONES = {
  available: styles.barAvailable,
  sale: styles.barSale,
};

/**
 * One project in a grid.
 *
 * The card is only a link when the project has a detail page written for
 * it. Previously every card on the home page was an anchor, so the two
 * projects with no page to go to still looked clickable and dumped the
 * visitor on the generic projects index — a control that lied about where
 * it led. Deriving the element from the data makes that state
 * unrepresentable.
 */
export function ProjectCard({ project, wide = false }) {
  const linkable = isLinkable(project);
  const className = [styles.card, wide ? styles.wide : ''].filter(Boolean).join(' ');

  /* The labels sit in one of two places depending on what the card has to
     show. Over a photograph they are pinned to a corner, because there is
     an image to label. On a card with no rendering yet there is nothing to
     pin them to — floating them over the facts panel just lands them on
     top of the note — so they run in the panel's normal flow instead. */
  const badgeList = project.badges?.length ? (
    project.badges.map(({ text, tone }) => (
      <span key={text} className={`${styles.badge} ${BADGE_TONES[tone] ?? ''}`.trim()}>
        {text}
      </span>
    ))
  ) : null;

  const inner = (
    <>
      {/* Above the image and outside .body: the body is padded, and this
          bar runs edge to edge. Rendered at every size but only shown on
          small screens — see .statusBar. */}
      {project.statusBar ? (
        <div className={`${styles.statusBar} ${BAR_TONES[project.statusBar.tone] ?? BAR_TONES.sale}`}>
          <span>{project.statusBar.left}</span>
          <span>{project.statusBar.right}</span>
        </div>
      ) : null}
      <div className={`${styles.media} ${project.image ? '' : styles.mediaFacts}`.trim()}>
        {project.image && badgeList ? (
          <div className={styles.badges}>{badgeList}</div>
        ) : null}
        {project.image ? (
          <img src={project.image} alt={project.imageAlt ?? ''} loading="lazy" />
        ) : project.facts?.length ? (
          <div className={styles.facts}>
            <div className={styles.factsHead}>
              <span className={styles.factsNote}>{project.factsNote ?? 'Renderings coming soon'}</span>
              {badgeList ? <div className={styles.badgesInline}>{badgeList}</div> : null}
            </div>
            <dl className={styles.factsList}>
              {project.facts.map(({ label, value, redundantOnMobile }) => (
                <div
                  key={label}
                  className={`${styles.factRow} ${redundantOnMobile ? styles.factRowRedundantOnMobile : ''}`.trim()}
                >
                  <dt className={styles.factLabel}>{label}</dt>
                  <dd className={styles.factValue}>{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : (
          <PlaceholderArt icon={project.icon} />
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.bodyTop}>
          <h3>{project.title}</h3>
          {/* Address first: it is the most specific thing the card can
              say. `meta` remains for a project with no street address to
              publish yet, and `status` as a last resort. */}
          <span className={styles.meta}>
            {project.address ?? project.meta ?? project.status}
          </span>
        </div>
        {project.statusLine ? (
          <p className={styles.statusLine}>{project.statusLine}</p>
        ) : null}
      </div>
    </>
  );

  if (!linkable) {
    return <div className={className}>{inner}</div>;
  }

  return (
    <Link to={`/projects/${project.slug}`} className={className}>
      {inner}
    </Link>
  );
}
